/**
 * Tool settings — auto-save / restore tool UI state via localStorage.
 *
 * Loaded as a synchronous (non-defer) script in the layout BEFORE
 * timbrica-core.js so the global is live when subsequent inline
 * tool scripts reference it at parse-time.
 *
 * Why a dedicated file: previously this was both inline-stubbed in
 * app.blade.php (3-layer defense for content-blocker resilience) AND
 * implemented in timbrica-core.js. Two sources of truth, drift risk
 * (Sentry sweep 2026-05-06 review). Single file is the canonical
 * impl; the inline 3-layer stub in app.blade.php remains as the
 * absolute-last-resort fallback for the case where BOTH this script
 * and timbrica-core.js fail to load (Chromium content-blocker
 * stripping multiple <script> tags).
 *
 * Surface:
 *   __toolSettings.save(tool, key, value)  — persist any JSON-serialisable value;
 *       returns false when the browser refused the write (full quota,
 *       blocked storage). Until 2026-10-09 it returned nothing and the
 *       refusal was swallowed, so a tool's «saved» could be a full disk.
 *   __toolSettings.load(tool, key, fallback) — read with fallback default
 *   __toolSettings.clear(tool) — remove every key for this tool
 *   __toolSettings.bind(tool, el, key, fallback) — auto-wire <input> to a key
 *
 * Storage prefix is `ts_<tool>_<key>` — single source of truth; if you
 * change it, change it here.
 */
(function(){
    'use strict';
    var prefix = 'ts_';

    function hasOption(sel, v){
        var s = String(v), o = sel.options || [];
        for (var i = 0; i < o.length; i++) if (String(o[i].value) === s) return true;
        return false;
    }

    var impl = {
        save: function(tool, key, val){
            try { localStorage.setItem(prefix + tool + '_' + key, JSON.stringify(val)); return true; } catch(_){ return false; }
        },
        load: function(tool, key, fallback){
            try {
                var v = localStorage.getItem(prefix + tool + '_' + key);
                return v !== null ? JSON.parse(v) : fallback;
            } catch(_) { return fallback; }
        },
        clear: function(tool){
            try {
                var ks = [];
                for (var i = 0; i < localStorage.length; i++){
                    var k = localStorage.key(i);
                    if (k && k.indexOf(prefix + tool + '_') === 0) ks.push(k);
                }
                ks.forEach(function(k){ localStorage.removeItem(k); });
            } catch(_){}
        },
        bind: function(tool, el, key, fallback){
            if (!el) return;
            var saved = this.load(tool, key, fallback);
            if (saved !== undefined && saved !== null){
                if (el.type === 'checkbox') el.checked = !!saved;
                else if (el.type === 'range') {
                    el.value = saved;
                    try { el.dispatchEvent(new Event('input')); } catch(_){}
                }
                /* <select> получает только значение, которое в нём ЕСТЬ. Иначе
                   браузер снимает выбор совсем (selectedIndex −1), поле стоит
                   пустым, а тул читает '' и подставляет своё число мимо гейта.
                   Так было у 34 привязок с умолчанием '' (`bind(…, '')`): у
                   /add-music-to-video пустой «Битрейт аудио» у всех, и 49 %
                   гостевых экспортов за 30 дней ушли выше потолка 128 кбит/с
                   (замер 08.10.2026). Нет такого варианта — остаётся выбор
                   самой страницы, и гейт ниже видит его, как видит всякий. */
                else if (el.tagName === 'SELECT' && !hasOption(el, saved)) { /* выбор страницы остаётся */ }
                else el.value = saved;
            }
            /* A restore lands AFTER quality-tier.js has gated the control, and a
               <select> assignment fires no `change` — so the saved value silently
               won the argument and the export ceiling stopped binding. MEASURED
               on prod 2026-07-31: an anonymous user exported 192 kbps from
               /audio-mixer and 1080p from /audio-to-video while each page's own
               line read «ваш тариф — 128 кбит/с» / «720p». Re-gate right here, at
               the one place every restore passes through, instead of patching it
               into each tool: gate() moves the value down to the best allowed
               option and shows the «качество снижено до …» note, so the ceiling
               binds and the change is never silent. __qtGating is the module's
               own re-entry guard — gate() dispatches a synthetic `change`, and
               without the flag that would run the whole pass twice. */
            if (el.getAttribute && el.getAttribute('data-quality-gate')
                && window.__qualityTier && typeof window.__qualityTier.gate === 'function') {
                el.__qtGating = true;
                try { window.__qualityTier.gate(el, el.getAttribute('data-quality-gate')); }
                catch(_){}
                finally { el.__qtGating = false; }
            }
            var self = this;
            el.addEventListener('input',  function(){ self.save(tool, key, el.type === 'checkbox' ? el.checked : el.value); });
            el.addEventListener('change', function(){ self.save(tool, key, el.type === 'checkbox' ? el.checked : el.value); });
        },

        /* ── Global metric/imperial preference (shared across ALL unit-aware tools) ──
           ONE preference, applied everywhere: set it in any tool (or in the profile) and every
           metric/imperial tool follows — no per-tool fragmentation. Anonymous users get it via
           localStorage (the majority); logged-in users are seeded from their profile (or
           signup-country default) through window.__USER_UNITS and synced back on change. */
        units: function(){
            var u = this.load('_global', 'unitSystem', null);
            if (u === 'metric' || u === 'imperial') return u;
            if (window.__USER_UNITS === 'metric' || window.__USER_UNITS === 'imperial') return window.__USER_UNITS;
            return 'metric';   // world default; US-style users flip once (then it sticks everywhere)
        },
        setUnits: function(u){
            if (u !== 'metric' && u !== 'imperial') return;
            this.save('_global', 'unitSystem', u);
            try { if (typeof window.__syncUserUnits === 'function') window.__syncUserUnits(u); } catch(_){}
        }
    };

    // Replace the inline stub from app.blade.php (which is identical in
    // shape but registered with __isStub=true) with the canonical impl.
    // Tests/inspection: `window.__toolSettings.__isStub === true` means
    // both this file AND timbrica-core.js failed to load and the user
    // is running on the absolute-last-resort inline stub.
    window.__toolSettings = impl;
})();
